// MY DAY (spec §109): a calm morning overview, "Start my day" — one prioritised queue with the client,
// the reason, a quick action, the outcome and the next action — and an evening review (completed ·
// pending · move to tomorrow). Moving keeps the first due date and every move on the task. The evening review opens with
// the structured end-of-day report «Απολογισμός ημέρας» (day-report.tsx, handoff v08 #25).
import { useMemo, useState } from 'react';
import { addDays, createTask, logContact, rescheduleTask, runSegment, type Outcome, type Task } from '@vero/domain';
import { attentionByPerson, type AttentionItem } from '@vero/intelligence';
import { useRuntime } from '../core/runtime.tsx';
import { Empty, PageHead, PersonCell, QuickDates, Seg, dayPart, reasonText } from '../core/ui.tsx';
import { DateField } from '../core/date-field.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate } from '../core/router.ts';
import { telLink, waLink } from '../core/links.ts';
import { performAction, RULE_ICON } from './attention.tsx';
import { startFlow } from './flow.tsx';
import { openers } from './forms.tsx';
import { DailyReportCard } from './day-report.tsx';
import { DailyGoalsCard } from './goals.tsx';

type Ev = { target: HTMLInputElement };

export function DayView() {
  const rt = useRuntime();
  const { t } = rt;
  const [tab, setTab] = useState<'morning' | 'queue' | 'evening'>(dayPart() === 'evening' ? 'evening' : 'morning');
  return (
    <div data-testid="day">
      <PageHead title={t(`day.greeting.${dayPart()}`, { name: rt.advisor.name?.split(' ')[0] || 'none' })} subtitle={rt.i18n.date(rt.today, 'long')} actions={<button className="btn" onClick={() => startFlow(rt)}><Icon name="flow" />{t('day.focus_mode')}</button>} />
      <div className="people-toolbar"><Seg values={['morning', 'queue', 'evening'] as const} value={tab} onChange={setTab} label={(x) => t(`day.tab.${x}`)} testId="day-tabs" /></div>
      {tab === 'morning' ? <Morning onStart={() => setTab('queue')} /> : tab === 'queue' ? <Queue /> : <Evening />}
    </div>
  );
}

function Morning({ onStart }: { onStart: () => void }) {
  const rt = useRuntime();
  const { t, view, today, attention } = rt;
  const count = (rules: string[]) => new Set(attention.filter((a) => rules.includes(a.rule)).map((a) => a.personId ?? a.prospectId)).size;
  const tasksToday = view.rows('tasks').filter((x) => x.status === 'open' && x.dueDate === today).length;
  const childBirthdays = useMemo(() => runSegment(view, 'birthdays_this_month', today).members.filter((m) => Number(m.detail.children) > 0).length, [view, today]);
  const tiles: [string, number, string][] = [
    ['day.tile.followups', tasksToday + count(['task_overdue']), '/agenda'],
    ['day.tile.birthdays', count(['birthday']), '/now'],
    ['day.tile.namedays', count(['nameday']), '/now'],
    ['day.tile.child_birthdays', childBirthdays, '/campaigns'],
    ['day.tile.renewals', count(['renewal_soon', 'insured_due']), '/renewals'],
    ['day.tile.reviews', count(['review_overdue', 'review_never', 'review_due_soon', 'kyc_review']), '/reviews'],
    ['day.tile.quotes', count(['quote_expiring', 'decision_pending']), '/decisions'],
    ['day.tile.lapses', count(['policy_lapsed', 'payment_late', 'premium_overdue']), '/retention'],
    ['day.tile.cases', count(['case_stalled', 'case_missing', 'case_no_next']), '/cases'],
    ['day.tile.life_events', count(['life_event', 'education_milestone', 'loan_maturity']), '/protection'],
  ];
  const total = attention.length;
  return (
    <div className="stack" data-testid="day-morning">
      <DailyGoalsCard day={today} />
      <section className="card card-pad calm">
        <p style={{ margin: 0, fontSize: 17 }}>{total ? t('day.morning_line', { count: new Set(attention.map((a) => a.personId ?? a.prospectId)).size }) : t('day.all_clear')}</p>
        <div className="row" style={{ marginTop: 14 }}><button className="btn btn-primary btn-lg" onClick={onStart} data-testid="start-my-day"><Icon name="now" />{t('day.start')}</button></div>
      </section>
      <div className="targets">{tiles.map(([k, n, path]) => <button key={k} className="target card tile" onClick={() => navigate(path)} data-testid="day-tile"><span className="upper">{t(k)}</span><b className="num">{n}</b></button>)}</div>
      <p className="tiny muted">{t('day.tiles_note')}</p>
    </div>
  );
}

function Queue() {
  const rt = useRuntime();
  const { t, attention } = rt;
  const groups = useMemo(() => [...attentionByPerson(attention).values()].slice(0, 25), [attention]);
  if (!groups.length) return <section className="card"><Empty icon="check">{t('day.all_clear')}</Empty></section>;
  return <div className="stack" data-testid="day-queue">{groups.map((items, i) => <QueueRow key={items[0]!.id} items={items} rank={i + 1} />)}</div>;
}
function QueueRow({ items, rank }: { items: AttentionItem[]; rank: number }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const lead = items[0]!;
  const person = lead.personId ? view.get('persons', lead.personId) : undefined;
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [next, setNext] = useState(addDays(rt.today, 7));
  const record = async () => {
    if (!person || !outcome) return;
    await rt.run((v, c) => {
      const contact = logContact(v, { personId: person.id, kind: 'call', outcome, title: reasonText(i18n, lead) }, c);
      const follow = createTask(v, { personId: person.id, kind: 'followup', title: t('day.next_action'), dueDate: next }, c);
      return { action: 'day.outcome', summary: person.displayName, puts: [...contact.puts, ...follow.puts, ...rt.snoozeCommand(lead.id)(v, c).puts], deletes: [] };
    }, 'day.recorded');
  };
  return (
    <article className="card card-pad queue-row" data-testid="queue-row">
      <div className="row spread" style={{ alignItems: 'flex-start', gap: 10 }}>
        <div className="row" style={{ gap: 10, minWidth: 0 }}><span className="rank">{rank}</span><PersonCell id={lead.personId} sub={items.map((x) => reasonText(i18n, x)).slice(0, 3).join(' · ')} /></div>
        <span className="row" style={{ gap: 4 }}>{items.slice(0, 4).map((x) => <Icon key={x.id} name={RULE_ICON[x.rule] ?? 'spark'} />)}</span>
      </div>
      <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
        <button className="btn btn-sm btn-primary" onClick={() => performAction(rt, lead)} data-testid="queue-action">{t(lead.actionKey)}</button>
        {person?.primaryPhone && <a className="btn btn-sm" href={telLink(person.primaryPhone)}><Icon name="call" />{t('common.call')}</a>}
        {person?.primaryPhone && <a className="btn btn-sm" href={waLink(person.primaryPhone)} target="_blank" rel="noreferrer"><Icon name="chat" />WhatsApp</a>}
        <button className="btn btn-sm btn-ghost" onClick={() => void rt.snooze(lead.id)} data-testid="queue-snooze">{t('flow.snooze')}</button>
      </div>
      {person && <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
        <Seg values={['positive', 'neutral', 'negative'] as const} value={(outcome ?? 'neutral') as Outcome} onChange={setOutcome} label={(x) => i18n.enumLabel('outcome', x)} testId="queue-outcome" />
        <span className="small muted">{t('day.next_action')}</span><DateField value={next} onChange={(d) => setNext(d)} label={t('day.next_action')} />
        <button className="btn btn-sm" disabled={!outcome} onClick={() => void record()} data-testid="queue-record">{t('day.record')}</button>
      </div>}
    </article>
  );
}

/** the next Monday–Friday after `d` (weekends are skipped for moved work) */
function nextWorkingDay(d: string): string { let x = addDays(d, 1); while ([0, 6].includes(new Date(`${x}T12:00:00`).getDay())) x = addDays(x, 1); return x; }

function Evening() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const tasks = view.rows('tasks');
  const completed = tasks.filter((x) => x.status === 'done' && x.completedAt?.slice(0, 10) === today);
  const pending = tasks.filter((x) => x.status === 'open' && x.dueDate && x.dueDate <= today).sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
  const [target, setTarget] = useState(nextWorkingDay(today));
  const moveAll = async () => { for (const x of pending) await rt.run((v, c) => rescheduleTask(v, x.id, target, c)); rt.toast(t('day.moved', { count: pending.length }), 'success'); };
  return (
    <div className="stack" data-testid="day-evening">
    <DailyReportCard />
    <div className="grid grid-2">
      <section className="card"><div className="card-head"><h3>{t('day.completed')} · {completed.length}</h3></div>
        {completed.length ? <div className="agenda-list">{completed.map((x) => <div key={x.id} className="agenda-item" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}><PersonCell id={x.personId} sub={i18n.maybe(x.title)} />{x.outcome ? <span className={`chip ${x.outcome === 'positive' ? 'ok' : x.outcome === 'negative' ? 'crit' : ''}`}>{i18n.enumLabel('outcome', x.outcome)}</span> : null}</div>)}</div> : <Empty icon="task">{t('day.none_completed')}</Empty>}
      </section>
      <section className="card"><div className="card-head"><h3>{t('day.pending')} · {pending.length}</h3>{pending.length > 0 && <button className="btn btn-sm" onClick={() => void moveAll()} data-testid="move-all">{t('day.move_all')}</button>}</div>
        <div style={{ padding: '0 20px' }}><QuickDates value={target} onChange={setTarget} /></div>
        {pending.length ? <div className="agenda-list">{pending.map((x) => <PendingRow key={x.id} task={x} target={target} />)}</div> : <Empty icon="check">{t('day.nothing_pending')}</Empty>}
      </section>
    </div>
    </div>
  );
}
function PendingRow({ task, target }: { task: Task; target: string }) {
  const rt = useRuntime();
  const { t, i18n } = rt;
  return (
    <div className="agenda-item" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }} data-testid="pending-row">
      <PersonCell id={task.personId} sub={`${i18n.maybe(task.title)} · ${i18n.date(task.dueDate!)}${task.originalDueDate && task.originalDueDate !== task.dueDate ? ` · ${t('day.first_due', { date: i18n.date(task.originalDueDate) })}` : ''}${task.rescheduleCount ? ` · ${t('day.moved_times', { count: task.rescheduleCount })}` : ''}`} />
      <div className="row" style={{ gap: 4 }}>
        <button className="btn btn-sm" onClick={() => openers(rt).complete(task)}>{t('task.complete')}</button>
        <button className="btn btn-sm btn-ghost" onClick={() => void rt.run((v, c) => rescheduleTask(v, task.id, target, c), 'day.moved_one')} data-testid="move-one">{t('day.move_to', { date: i18n.date(target, 'dayMonth') })}</button>
      </div>
    </div>
  );
}
